import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertTriangle,
  Search,
  Package,
  ArrowRight,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
  Settings,
  Calendar,
  Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { Family } from '@/types/family';
import { Product, ProductFamily, productService, QUERY_KEYS } from '@/services/productService';
import { getFamilies } from '@/api/familyApi';
import { getAttributeGroups } from '@/api/attributeGroupApi';
import { useAuth } from '@/contexts/AuthContext';
import { logError } from '@/utils/errorHandler';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

interface FamilyChangeModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly product: Product;
  readonly onFamilyChanged?: (updatedProduct: Product) => void;
}

interface FamilyComparisonData {
  currentFamily: Family | null;
  newFamily: Family | null;
  attributeGroupsToRemove: string[];
  attributeGroupsToAdd: string[];
  potentialDataLoss: boolean;
}

export function FamilyChangeModal({
  isOpen,
  onClose,
  product,
  onFamilyChanged,
}: FamilyChangeModalProps) {
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showComparison, setShowComparison] = useState(false);

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { getFamilyChangeModalText } = useProductDetailPageTranslations();

  // Helper function to get family ID from product family property
  const getCurrentFamilyId = useCallback((): number | null => {
    if (!product.family) return null;
    if (typeof product.family === 'number') return product.family;
    if (typeof product.family === 'object' && 'id' in product.family) {
      return product.family.id;
    }
    return null;
  }, [product.family]);

  // Helper function to normalize family data
  const normalizeFamily = (family: ProductFamily | Family): Family => {
    return {
      id: family.id,
      code: family.code ?? '',
      label: family.label ?? ('name' in family ? family.name : '') ?? '',
      description: family.description,
      created_at: 'created_at' in family ? family.created_at : undefined,
      updated_at: 'updated_at' in family ? family.updated_at : undefined,
      attribute_groups: 'attribute_groups' in family ? family.attribute_groups : undefined,
    };
  };

  // Fetch all families
  const {
    data: families = [],
    isLoading: familiesLoading,
    error: familiesError,
  } = useQuery({
    queryKey: ['families'],
    queryFn: getFamilies,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch all attribute groups for name lookup
  const { data: attributeGroups = [], isLoading: attributeGroupsLoading } = useQuery({
    queryKey: ['attributeGroups'],
    queryFn: getAttributeGroups,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create a lookup map for attribute group names
  const attributeGroupNameMap = useMemo(() => {
    const map = new Map<number, string>();
    attributeGroups.forEach(group => {
      map.set(group.id, group.name);
    });
    return map;
  }, [attributeGroups]);

  // Update product family mutation
  const updateFamilyMutation = useMutation({
    mutationFn: async (familyId: number | null) => {
      if (!product.id) {
        throw new Error('Product ID is required');
      }
      return await productService.updateProduct(product.id, {
        family: familyId ?? undefined,
        _activity_type: 'FAMILY_CHANGE',
      });
    },
    onSuccess: updatedProduct => {
      if (product.id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(product.id) });
      }
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });

      toast.success(getFamilyChangeModalText('notifications.updateSuccess'));

      if (onFamilyChanged) {
        onFamilyChanged(updatedProduct);
      }

      handleClose();
    },
    onError: (error: unknown) => {
      logError(error instanceof Error ? error : 'Error updating family', {
        component: 'FamilyChangeModal',
        action: 'updateFamily',
        metadata: { productId: product.id, selectedFamilyId },
      });
      toast.error(
        (error as any).response?.data?.message ??
          (error as any).message ??
          getFamilyChangeModalText('notifications.updateFailed')
      );
    },
  });

  // Filter families based on search term
  const filteredFamilies = useMemo(() => {
    if (!searchTerm.trim()) return families;

    const term = searchTerm.toLowerCase();
    return families.filter(
      family =>
        family.label?.toLowerCase().includes(term) ||
        family.code?.toLowerCase().includes(term) ||
        family.description?.toLowerCase().includes(term)
    );
  }, [families, searchTerm]);

  // Helper function to get attribute group name by ID
  const getAttributeGroupName = useCallback(
    (groupId: number): string => {
      // First try the attribute groups lookup map
      const nameFromMap = attributeGroupNameMap.get(groupId);
      if (nameFromMap) {
        return nameFromMap;
      }

      // Fallback: try to find the group name in any of the families
      for (const family of families) {
        if (family.attribute_groups) {
          for (const ag of family.attribute_groups) {
            if (ag.attribute_group === groupId) {
              // Try to get name from attribute_group_object first, then fallback
              return ag.attribute_group_object?.name ?? `Attribute Group ${groupId}`;
            }
          }
        }
      }
      return `Attribute Group ${groupId}`;
    },
    [attributeGroupNameMap, families]
  );

  // Get comparison data when a family is selected
  const comparisonData = useMemo((): FamilyComparisonData => {
    // Get current family as normalized Family object
    let currentFamily: Family | null = null;
    if (product.family) {
      if (typeof product.family === 'object') {
        currentFamily = normalizeFamily(product.family);
      } else {
        // If it's just an ID, try to find it in the families list
        const familyId =
          typeof product.family === 'number' ? product.family : parseInt(product.family);
        const foundFamily = families.find(f => f.id === familyId);
        if (foundFamily) {
          currentFamily = foundFamily;
        }
      }
    }

    const newFamily = selectedFamilyId
      ? (families.find(f => f.id === selectedFamilyId) ?? null)
      : null;

    // Get attribute groups for comparison with improved name resolution
    const currentGroups =
      currentFamily?.attribute_groups?.map(ag => {
        // Try multiple ways to get the group name
        if (ag.attribute_group_object?.name) {
          return ag.attribute_group_object.name;
        }
        // Fallback to looking up by ID in all families
        return getAttributeGroupName(ag.attribute_group);
      }) ?? [];

    const newGroups =
      newFamily?.attribute_groups?.map(ag => {
        // Try multiple ways to get the group name
        if (ag.attribute_group_object?.name) {
          return ag.attribute_group_object.name;
        }
        // Fallback to looking up by ID in all families
        return getAttributeGroupName(ag.attribute_group);
      }) ?? [];

    const attributeGroupsToRemove = currentGroups.filter(group => !newGroups.includes(group));

    const attributeGroupsToAdd = newGroups.filter(group => !currentGroups.includes(group));

    const potentialDataLoss = attributeGroupsToRemove.length > 0;

    return {
      currentFamily,
      newFamily,
      attributeGroupsToRemove,
      attributeGroupsToAdd,
      potentialDataLoss,
    };
  }, [product.family, selectedFamilyId, families, getAttributeGroupName]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedFamilyId(getCurrentFamilyId());
      setSearchTerm('');
      setShowComparison(false);
    }
  }, [isOpen, getCurrentFamilyId]);

  // Handle family selection
  const handleFamilySelect = (familyId: number) => {
    setSelectedFamilyId(familyId);
    setShowComparison(true);
  };

  // Handle family selection with keyboard
  const handleFamilySelectKeyboard = (event: React.KeyboardEvent, familyId: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleFamilySelect(familyId);
    }
  };

  // Handle removing family (set to null)
  const handleRemoveFamily = () => {
    setSelectedFamilyId(null);
    setShowComparison(true);
  };

  // Handle removing family with keyboard
  const handleRemoveFamilyKeyboard = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRemoveFamily();
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedFamilyId(null);
    setSearchTerm('');
    setShowComparison(false);
    onClose();
  };

  // Handle save
  const handleSave = () => {
    updateFamilyMutation.mutate(selectedFamilyId);
  };

  // Render attribute groups to remove
  const renderAttributeGroupsToRemove = () => {
    if (comparisonData.attributeGroupsToRemove.length === 0) return null;

    return (
      <Alert variant="destructive">
        <AlertTriangle className="size-4" />
        <AlertDescription>
          <div className="space-y-2">
            <p className="font-medium">
              {getFamilyChangeModalText('comparison.groupsWillBeRemoved')}
            </p>
            <ul className="list-inside list-disc space-y-1">
              {comparisonData.attributeGroupsToRemove.map(group => (
                <li key={`remove-${group}`} className="text-sm">
                  {group}
                </li>
              ))}
            </ul>
            <p className="text-sm font-medium text-destructive">
              {getFamilyChangeModalText('comparison.permanentDataLoss')}
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  };

  // Render attribute groups to add
  const renderAttributeGroupsToAdd = () => {
    if (comparisonData.attributeGroupsToAdd.length === 0) return null;

    return (
      <Alert>
        <Info className="size-4" />
        <AlertDescription>
          <div className="space-y-2">
            <p className="font-medium">
              {getFamilyChangeModalText('comparison.groupsWillBeAdded')}
            </p>
            <ul className="list-inside list-disc space-y-1">
              {comparisonData.attributeGroupsToAdd.map(group => (
                <li key={`add-${group}`} className="text-sm">
                  {group}
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">
              {getFamilyChangeModalText('comparison.groupsAvailableAfterChange')}
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  };

  // Check if there are changes
  const hasChanges = selectedFamilyId !== getCurrentFamilyId();
  const isLoading = updateFamilyMutation.isPending;
  const isDataLoading = familiesLoading ?? attributeGroupsLoading;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="flex h-full max-h-[95vh] w-[95vw] max-w-7xl flex-1 flex-col overflow-hidden px-6 py-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="size-5" />
            {getFamilyChangeModalText('title')}
          </DialogTitle>
          <DialogDescription>
            {getFamilyChangeModalText('description', { productName: product.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-hidden">
          {familiesError ? (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription>{getFamilyChangeModalText('search.errorLoading')}</AlertDescription>
            </Alert>
          ) : (
            <div className="grid h-full min-h-0 grid-cols-1 gap-6 px-4 lg:grid-cols-2">
              {/* Family Selection Panel */}
              <div className="flex h-full min-h-0 flex-col space-y-4">
                <div>
                  <Label htmlFor="family-search">{getFamilyChangeModalText('search.label')}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="family-search"
                      placeholder={getFamilyChangeModalText('search.placeholderDetailed')}
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{getFamilyChangeModalText('tabs.selectFamily')}</Label>
                    <Badge variant="outline">
                      {getFamilyChangeModalText('search.familiesCount', {
                        count: filteredFamilies.length,
                      })}
                    </Badge>
                  </div>

                  {/* Remove Family Option */}
                  <div
                    className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                      selectedFamilyId === null
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={handleRemoveFamily}
                    onKeyDown={handleRemoveFamilyKeyboard}
                    role="button"
                    tabIndex={0}
                    aria-label="Remove family assignment"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <XCircle className="size-4 text-muted-foreground" />
                        <span className="font-medium text-muted-foreground">
                          {getFamilyChangeModalText('familyCard.removeFamily')}
                        </span>
                      </div>
                      {selectedFamilyId === null && <CheckCircle className="size-4 text-primary" />}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {getFamilyChangeModalText('familyCard.noFamilyDescription')}
                    </p>
                  </div>

                  <div className="flex-1 overflow-y-auto rounded-lg border">
                    <div className="space-y-2 p-2">
                      {isDataLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="size-6 animate-spin" />
                          <span className="ml-2">
                            {getFamilyChangeModalText('search.loadingFamilies')}
                          </span>
                        </div>
                      ) : filteredFamilies.length === 0 ? (
                        <div className="py-8 text-center text-muted-foreground">
                          {searchTerm
                            ? getFamilyChangeModalText('search.noResults')
                            : getFamilyChangeModalText('search.noResults')}
                        </div>
                      ) : (
                        filteredFamilies.map(family => (
                          <div
                            key={family.id}
                            className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                              selectedFamilyId === family.id
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50'
                            }`}
                            onClick={() => handleFamilySelect(family.id)}
                            onKeyDown={e => handleFamilySelectKeyboard(e, family.id)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Select family ${family.label}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Package className="size-4" />
                                <span className="font-medium">{family.label}</span>
                                <Badge variant="outline" className="text-xs">
                                  {family.code}
                                </Badge>
                              </div>
                              {selectedFamilyId === family.id && (
                                <CheckCircle className="size-4 text-primary" />
                              )}
                            </div>
                            {family.description && (
                              <p className="mt-1 text-sm text-muted-foreground">
                                {family.description}
                              </p>
                            )}
                            <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Hash className="size-3" />
                                ID: {family.id}
                              </span>
                              {family.attribute_groups && (
                                <span className="flex items-center gap-1">
                                  <Settings className="size-3" />
                                  {family.attribute_groups.length} attribute groups
                                </span>
                              )}
                              {family.created_at && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="size-3" />
                                  {format(new Date(family.created_at), 'MMM yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Panel */}
              <div className="flex h-full flex-col space-y-4">
                <Label>{getFamilyChangeModalText('comparison.title')}</Label>

                {showComparison ? (
                  <div className="flex-1 overflow-y-auto rounded-lg border p-4">
                    <div className="space-y-4">
                      {/* Current vs New Family */}
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            {getFamilyChangeModalText('comparison.currentFamily')}
                          </Label>
                          <div className="rounded-lg border bg-muted/20 p-3">
                            {comparisonData.currentFamily ? (
                              <div>
                                <div className="font-medium">
                                  {comparisonData.currentFamily.label}
                                </div>
                                <Badge variant="outline" className="mt-1">
                                  {comparisonData.currentFamily.code}
                                </Badge>
                                {comparisonData.currentFamily.description && (
                                  <p className="mt-1 text-sm text-muted-foreground">
                                    {comparisonData.currentFamily.description}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">
                                {getFamilyChangeModalText('states.noFamilyAssigned')}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            {getFamilyChangeModalText('comparison.newFamily')}
                          </Label>
                          <div className="rounded-lg border bg-primary/5 p-3">
                            {comparisonData.newFamily ? (
                              <div>
                                <div className="font-medium">{comparisonData.newFamily.label}</div>
                                <Badge variant="outline" className="mt-1">
                                  {comparisonData.newFamily.code}
                                </Badge>
                                {comparisonData.newFamily.description && (
                                  <p className="mt-1 text-sm text-muted-foreground">
                                    {comparisonData.newFamily.description}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">
                                {getFamilyChangeModalText('states.noFamilyAssigned')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Attribute Groups Changes */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">
                          {getFamilyChangeModalText('comparison.attributeGroupsChanges')}
                        </Label>

                        {renderAttributeGroupsToRemove()}
                        {renderAttributeGroupsToAdd()}

                        {comparisonData.attributeGroupsToRemove.length === 0 &&
                          comparisonData.attributeGroupsToAdd.length === 0 && (
                            <Alert>
                              <CheckCircle className="size-4" />
                              <AlertDescription>
                                {getFamilyChangeModalText('comparison.safeOperation')}
                              </AlertDescription>
                            </Alert>
                          )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-1 items-center justify-center rounded-lg border text-muted-foreground">
                    <div className="text-center">
                      <ArrowRight className="mx-auto mb-2 size-8" />
                      <p>{getFamilyChangeModalText('comparison.selectFamilyToAnalyze')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div className="hidden items-center text-sm text-muted-foreground sm:flex">
            {user && (
              <span>
                {getFamilyChangeModalText('comparison.changesLoggedBy', {
                  userName: user.name ?? user.email,
                })}
              </span>
            )}
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 sm:flex-none"
            >
              {getFamilyChangeModalText('buttons.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isLoading}
              className="flex-1 sm:flex-none"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {getFamilyChangeModalText('buttons.saving')}
                </>
              ) : (
                getFamilyChangeModalText('buttons.save')
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FamilyChangeModal;
